import { ApiError } from '@/app/lib/apiError'
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router'

import { coverageRowFor, useInstrumentSources, useQuantData } from '@/app/hooks/useQuantData'
import { QuantLivePreview } from '@/app/components/QuantLivePreview'
import type { PreviewMeta } from '@/app/hooks/useQuantPreview'
import { mergeLiveSession, refreshLiveSessions } from '@/app/hooks/useQuantLiveSessions'
import { refreshQuantRuns } from '@/app/hooks/useQuantRuns'
import { useQuantSavedConfigs } from '@/app/hooks/useQuantSavedConfigs'
import { QuantSavedConfigs } from '@/app/components/QuantSavedConfigs'
import { rawFromSaved } from '@/app/lib/quant/savedConfigs'
import { refreshQuantStrategies } from '@/app/hooks/useQuantStrategies'
import { useSignalLog } from '@/app/lib/signalLog'
import {
  defaultLabel,
  flagsFor,
  getAccountForm,
  getConfigForm,
  initialConfigForm,
  isParamChanged,
  saveAccountForm,
  saveConfigForm,
  takeLoadedNote,
  type ConfigFormState,
} from '@/app/lib/quant/configFormState'
import type { QuantFeedState } from '@/app/lib/quant/feedView'
import { coerceAll } from '@/app/lib/quant/coerceParam'
import { dateBounds, dateRangeError, dateTextError } from '@/app/lib/quant/dateBounds'
import { formatInstrument } from '@/app/lib/quant/formatInstrument'
import { defaultTf, previewUnavailableReason } from '@/app/lib/quant/previewOptions'
import { mismatchLine, noPreviewReason, resolveInstrumentOffer } from '@/app/lib/quant/previewView'
import {
  accountFormFrom,
  buildLiveStartBody,
  isAccountChanged,
  NO_LIVE_FEED,
  startLiveControl,
  validateAccount,
  type AccountField,
  type AccountForm,
} from '@/app/lib/quant/liveAccount'
import { droppedNote } from '@/app/lib/quant/paramsForConfig'
import { liveRefusalKind, quantErrorMessage, refusalKind } from '@/app/lib/quant/quantErrors'
import { quantApi } from '@/app/lib/quant/quantApi'
import { schemaToParams } from '@/app/lib/quant/schemaToParams'
import { type DataCoverage, type ParamDef, type ParamValue, type SavedConfig, type Strategy } from '@/app/lib/quant/types'
import { Button } from '@/ui/Button'
import { cn } from '@/ui/lib/utils'
import { QuantCliPreview } from '@/ui/QuantCliPreview'
import { QuantParamRow } from '@/ui/QuantParamRow'
import { QuantPreviewDisabled } from '@/ui/QuantPreviewDisabled'

export interface QuantConfigPaneProps {
  strategy: Strategy
  /** Busy run slots and the cap; both absent until the runs list has loaded. */
  active: number | undefined
  max: number | undefined
  /** Every slot is busy and none is the caller's (spec 038 19.6). */
  heldByOther?: boolean
  /** Spec 040: the shell's market feed state; only used for the preview's "no live feed" hint. */
  feed?: QuantFeedState
  /** Spec 041: the last live list read's counters; null until it has succeeded (the server then decides). */
  live?: { active: number; max: number; ownActive: number } | null
}

const ACCOUNT_ROWS: Array<{ field: AccountField; name: string; kind: 'int' | 'float' }> = [
  { field: 'starting_balance', name: 'Starting balance', kind: 'float' },
  { field: 'commission_per_contract', name: 'Commission per contract', kind: 'float' },
  { field: 'max_contracts', name: 'Max contracts', kind: 'int' },
]

const FIRST_BAR_NOTE = 'A fresh ema_cross can take a position on its first bar.'

/**
 * The Config tab (spec 036 D-5, handoff 3.3): banners, the live preview chart, the Parameters card with the schema-driven form, the CLI preview,
 * Reset defaults and Start backtest. A strategy with a parse error shows the
 * config-error card instead and no form. The server stays authoritative for
 * coercion; client validation is only instant feedback.
 */
export function QuantConfigPane({
  strategy,
  active,
  max,
  heldByOther = false,
  feed,
  live = null,
}: QuantConfigPaneProps) {
  if (strategy.error !== null || strategy.params_schema === null) {
    return <ConfigErrorCard strategy={strategy} />
  }
  // Only a strategy that can show a preview reads the stored-data coverage (one `GET /quant/data` per mount).
  if (noPreviewReason(strategy) === null) {
    return (
      <ConfigFormWithCoverage
        strategy={strategy}
        active={active}
        max={max}
        heldByOther={heldByOther}
        feed={feed}
        live={live}
      />
    )
  }
  return (
    <ConfigForm
      strategy={strategy}
      active={active}
      max={max}
      heldByOther={heldByOther}
      feed={feed}
      live={live}
      coverage={null}
      coverageKnown
    />
  )
}

function ConfigFormWithCoverage(props: QuantConfigPaneProps) {
  const data = useQuantData()
  return (
    <ConfigForm
      {...props}
      coverage={data.status === 'loaded' ? data.data : null}
      coverageKnown={data.status === 'loaded'}
    />
  )
}

function ConfigErrorCard({ strategy }: { strategy: Strategy }) {
  return (
    <PaneColumn>
      <div className="bg-paper text-ink" data-config-error-card>
        <div className="flex h-10 items-center gap-3 border-b border-ink-20 px-[18px]">
          <span className="flex-1 font-sans text-[11px] font-bold tracking-[0.2em] uppercase">
            Parameters
          </span>
          <span className="shrink-0 font-mono text-[9.5px] tracking-[0.14em] text-alert uppercase">
            Config error · not runnable
          </span>
        </div>
        <div className="flex flex-col gap-2.5 px-[18px] pt-4 pb-[18px]">
          <div className="font-mono text-[12px] text-ink-50">
            strategies/{strategy.slug}/strategy.toml failed to parse. Fix the file on disk; the list
            rescans on reload.
          </div>
          <pre className="m-0 bg-ink px-3 py-3 font-mono text-[11.5px] leading-[1.6] whitespace-pre-wrap text-paper-90">
            {strategy.error}
          </pre>
        </div>
      </div>
    </PaneColumn>
  )
}

function PaneColumn({ children }: { children: ReactNode }) {
  return (
    <div className="absolute inset-0 overflow-y-auto">
      <div className="flex max-w-[1240px] flex-col gap-3.5 px-7 pt-5 pb-11">{children}</div>
    </div>
  )
}

function ConfigForm({
  strategy,
  active,
  max,
  heldByOther = false,
  feed,
  live: liveCap = null,
  coverage,
  coverageKnown,
}: QuantConfigPaneProps & { coverage: DataCoverage | null; coverageKnown: boolean }) {
  const navigate = useNavigate()
  const { emit } = useSignalLog()
  const defs = useMemo(() => schemaToParams(strategy.params_schema), [strategy.params_schema])
  const slug = strategy.slug

  const [form, setForm] = useState<ConfigFormState>(
    () => getConfigForm(slug) ?? initialConfigForm(defs),
  )
  const saved = useQuantSavedConfigs(slug)
  const [refused, setRefused] = useState<string | null>(null)
  const [startingKind, setStartingKind] = useState<'backtest' | 'live' | null>(null)
  const support = strategy.live != null && !strategy.live_error ? strategy.live : null
  const [account, setAccount] = useState<AccountForm | null>(() =>
    strategy.live == null ? null : (getAccountForm(slug) ?? accountFormFrom(strategy.live.account_defaults)),
  )
  const [accountServerError, setAccountServerError] = useState<string | null>(null)
  // Decided at mount (a hand-off from "Load params into config"), so it never shifts the card afterwards.
  const [droppedText, setDroppedText] = useState<string | null>(() => droppedNote(takeLoadedNote(slug) ?? []))
  // The server's `invalid_params` text for the form as shown (set by the live preview).
  const [previewInvalid, setPreviewInvalid] = useState<string | null>(null)
  const [previewMeta, setPreviewMeta] = useState<PreviewMeta | null>(null)
  const [refusedSeq, setRefusedSeq] = useState(0)
  const refusedRef = useRef<HTMLDivElement>(null)

  const { values, errors: coercedErrors } = useMemo(() => coerceAll(defs, form.raw), [defs, form.raw])
  const changedCount = defs.filter((def) => isParamChanged(def, form.raw[def.name])).length
  const capReached = active !== undefined && max !== undefined && active >= max
  const preview = strategy.preview ?? null
  const noPreview = noPreviewReason(strategy)
  const instrumentDef = defs.find((def) => def.name === 'instrument')
  const sources = useInstrumentSources(instrumentDef !== undefined || preview !== null)
  const instrumentLabel = (name: string) => formatInstrument(name, sources)
  const rawInstrument = form.raw.instrument
  const offer =
    preview === null
      ? null
      : resolveInstrumentOffer(
          preview,
          typeof rawInstrument === 'string' && rawInstrument !== '' ? rawInstrument : null,
          instrumentDef !== undefined && instrumentDef.options.length > 0
            ? instrumentDef.options
            : null,
        )
  // A timeframe the preview declares no bar limit for cannot run (the default one is checked here).
  const unavailable =
    preview === null ? null : previewUnavailableReason(preview, defaultTf(null, preview))

  // Stored-data span of the preview's instrument; date rows and "Use preview range" need it.
  const covRow = offer?.instrument != null ? coverageRowFor(coverage, offer.instrument) : null
  const startDef = defs.find((def) => def.name === 'start' && def.kind === 'str')
  const endDef = defs.find((def) => def.name === 'end' && def.kind === 'str')
  const hasRange = startDef !== undefined && endDef !== undefined
  // The preview's instrument drives "data through" and "Use preview range"; the date rows are bounded by the
  // instrument the run will use (the form's), which can differ from the previewed one.
  const lastDay = dateBounds(covRow).max
  const formInstrument =
    typeof rawInstrument === 'string' && rawInstrument !== '' ? rawInstrument : (offer?.instrument ?? null)
  const formRow = formInstrument !== null ? coverageRowFor(coverage, formInstrument) : null
  const bounds = dateBounds(formRow)
  // Start/end: a malformed date, or (with a stored span) a date outside it, is a row error that blocks Start.
  const errors = { ...coercedErrors }
  if (hasRange) {
    for (const name of ['start', 'end']) {
      const message = dateTextError(form.raw[name]) ?? dateRangeError(form.raw[name], bounds)
      if (message !== undefined) errors[name] = message
    }
  }
  const invalid = Object.keys(errors).length > 0
  const rangeFrom =
    previewMeta !== null && previewMeta.instrument === offer?.instrument
      ? previewMeta.input_range.from.slice(0, 10)
      : null
  let rangeReason: string | null = null
  if (!hasRange) rangeReason = 'No start and end params'
  else if (covRow === null || lastDay === null) rangeReason = 'No stored data for this instrument'
  else if (rangeFrom === null) rangeReason = 'Waiting for the preview'
  else if (rangeFrom > lastDay) rangeReason = 'The preview range starts after the last complete day'

  function dateProps(def: ParamDef) {
    if (!hasRange || (def.name !== 'start' && def.name !== 'end')) return {}
    return {
      inputType: 'date' as const,
      min: bounds.min,
      max: bounds.max,
      // No coverage row: both sides open, with a note once the coverage read has settled.
      warning:
        formRow === null
          ? coverageKnown && dateTextError(form.raw[def.name]) === undefined
            ? (bounds.note ?? undefined)
            : undefined
          : undefined,
    }
  }

  function useRange() {
    if (rangeFrom === null || lastDay === null) return
    // Stay inside the stored span: a preview range that starts before it is clamped to its first day.
    const from = bounds.min !== null && rangeFrom < bounds.min ? bounds.min : rangeFrom
    const to = bounds.max !== null && bounds.max < lastDay ? bounds.max : lastDay
    const raw = { ...form.raw, start: from, end: to }
    commit({ raw, lastValidFlags: flagsFor(defs, raw) ?? form.lastValidFlags })
    setRefused(null)
    emit('save', `Set start ${from} and end ${to} from the preview range.`)
  }

  // A refusal can land below the fold of a long form: bring the banner into view.
  useEffect(() => {
    if (refusedSeq > 0) refusedRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
  }, [refusedSeq])

  function commit(next: ConfigFormState) {
    setForm(next)
    saveConfigForm(slug, next)
  }

  function setField(name: string, value: ParamValue) {
    const raw = { ...form.raw, [name]: value }
    commit({ raw, lastValidFlags: flagsFor(defs, raw) ?? form.lastValidFlags })
    setRefused(null)
  }

  function loadSaved(config: SavedConfig) {
    const { raw, dropped } = rawFromSaved(config.params, defs)
    commit({ raw, lastValidFlags: flagsFor(defs, raw) ?? form.lastValidFlags })
    setRefused(null)
    setDroppedText(droppedNote(dropped))
  }

  function reset() {
    commit(initialConfigForm(defs))
    setRefused(null)
    setDroppedText(null)
    if (strategy.live != null) {
      const next = accountFormFrom(strategy.live.account_defaults)
      setAccount(next)
      saveAccountForm(slug, next)
      setAccountServerError(null)
    }
    emit('save', `Reset ${slug} parameters to defaults.`)
  }

  async function start() {
    if (startingKind !== null) return
    const result = coerceAll(defs, form.raw)
    const firstField = Object.keys(errors)[0]
    if (firstField !== undefined) {
      emit('err', `Run not sent - ${firstField}: ${errors[firstField]}`)
      return
    }
    setStartingKind('backtest')
    try {
      const run = await quantApi.startRun({ strategy: slug, params: result.values })
      setRefused(null)
      emit('save', `Run ${run.id} spawned - ${slug}.`)
      void refreshQuantRuns()
      refreshQuantStrategies()
      navigate(`/strategies/${slug}/runs/${run.id}`)
    } catch (error) {
      const message = quantErrorMessage(error)
      setRefused(message)
      setRefusedSeq((n) => n + 1)
      const kind = refusalKind(error)
      if (kind === 'refused') emit('warn', `Run refused - ${message}.`)
      else if (kind === 'rejected') emit('err', `Run rejected - ${message}`)
      // 5xx and network failures already wrote one err line in apiFetch.
      if (error instanceof ApiError) void refreshQuantRuns()
    } finally {
      setStartingKind(null)
    }
  }

  const ignoredNames = new Set(support?.ignored_params ?? [])
  const accountState = account === null ? null : validateAccount(account)
  const liveControl = startLiveControl({
    support: strategy.live,
    liveError: strategy.live_error,
    cap: liveCap,
    starting: startingKind === 'live',
    instrument: strategy.live?.instrument ?? null,
    liveInstruments: feed?.status === 'loaded' ? feed.feed.live_instruments : null,
  })

  function setAccountField(field: AccountField, value: string) {
    if (account === null) return
    const next = { ...account, [field]: value }
    setAccount(next)
    saveAccountForm(slug, next)
    setAccountServerError(null)
  }

  async function startLive() {
    if (startingKind !== null || support === null || account === null || !liveControl.enabled) return
    const overridden = new Set(Object.keys(support.defaults))
    const sent = coerceAll(
      defs.filter((def) => !ignoredNames.has(def.name) && !overridden.has(def.name)),
      form.raw,
    )
    const firstField = Object.keys(sent.errors)[0]
    if (firstField !== undefined) {
      emit('err', `Live start not sent - ${firstField}: ${sent.errors[firstField]}`)
      return
    }
    const checked = validateAccount(account)
    const firstAccount = Object.keys(checked.errors)[0] as AccountField | undefined
    if (firstAccount !== undefined || checked.values === null) {
      const row = ACCOUNT_ROWS.find((r) => r.field === firstAccount)
      emit('err', `Live start not sent - ${row?.name ?? 'account'}: ${firstAccount === undefined ? 'invalid' : checked.errors[firstAccount]}`)
      return
    }
    const body = buildLiveStartBody({ strategy: slug, values: sent.values, support, account: checked.values })
    setStartingKind('live')
    try {
      const session = await quantApi.startLiveSession(body)
      setRefused(null)
      mergeLiveSession(session)
      void refreshLiveSessions()
      emit('save', `Live session ${session.id} started - ${slug} (paper).`)
      navigate(`/strategies/${slug}/live/${session.id}`)
    } catch (error) {
      let message = quantErrorMessage(error)
      // The server's `instrument_not_live` is the backstop for the disabled control: same copy.
      if (error instanceof ApiError && error.code === 'instrument_not_live') message = NO_LIVE_FEED
      if (error instanceof ApiError && error.status === 0) {
        message = `${message} The session may still have been created; check the Live tab.`
      }
      setRefused(message)
      setRefusedSeq((n) => n + 1)
      const kind = liveRefusalKind(error)
      if (kind === 'refused') emit('warn', `Live start refused - ${message}.`)
      else if (kind === 'rejected') emit('err', `Live start rejected - ${message}`)
      if (error instanceof ApiError) {
        if (error.code === 'invalid_account') setAccountServerError(error.message)
        void refreshLiveSessions()
      }
    } finally {
      setStartingKind(null)
    }
  }

  const slotsText = max === 1 ? 'Slot busy' : `All ${max} slots busy`

  return (
    <PaneColumn>
      {capReached && (
        <div
          role="status"
          data-banner="slots-full"
          className="flex items-baseline gap-3.5 border border-[#CB8A00] px-3.5 py-2.5 font-mono text-[12px] leading-[1.5] text-[#CB8A00]"
        >
          <span className="shrink-0 font-sans text-[10px] font-bold tracking-[0.2em] uppercase">
            Slots full
          </span>
          <span>
            {heldByOther
              ? max === 1
                ? 'The run slot is held by another user. Wait for it to finish; you have no run here to cancel.'
                : `${active} of ${max} run slots are busy, none of them yours. Wait for one to finish; you have no run here to cancel.`
              : `${max === 1 ? 'The run slot is busy' : `${active} of ${max} run slots are busy`}. Wait for one to finish, or cancel it - runs are refused rather than silently queued.`}
          </span>
        </div>
      )}
      {refused !== null && (
        <div
          ref={refusedRef}
          role="alert"
          data-banner="refused"
          className="flex items-baseline gap-3.5 bg-alert px-3.5 py-2.5 font-mono text-[12px] leading-[1.5] text-paper"
        >
          <span className="shrink-0 font-sans text-[10px] font-bold tracking-[0.2em] uppercase">
            Refused
          </span>
          <span className="min-w-0 flex-1 [overflow-wrap:anywhere]">{refused}</span>
          <button
            type="button"
            onClick={() => setRefused(null)}
            className="shrink-0 cursor-pointer border border-paper bg-transparent px-2 py-0.5 font-mono text-[10px] tracking-[0.14em] text-paper uppercase"
          >
            Dismiss
          </button>
        </div>
      )}

      {droppedText !== null && (
        <div
          role="status"
          data-banner="params-dropped"
          className="flex h-10 shrink-0 items-center gap-3.5 border border-ink-25 px-3.5 font-mono text-[12px] text-paper"
        >
          <span className="min-w-0 flex-1 truncate" title={droppedText}>
            {droppedText}
          </span>
          <button
            type="button"
            onClick={() => setDroppedText(null)}
            className="shrink-0 cursor-pointer border border-paper bg-transparent px-2 py-0.5 font-mono text-[10px] tracking-[0.14em] text-paper uppercase"
          >
            Dismiss
          </button>
        </div>
      )}

      {noPreview !== null ? (
        <QuantPreviewDisabled reason={noPreview} />
      ) : offer !== null && offer.instrument === null ? (
        <QuantPreviewDisabled
          reason={offer.disabledReason ?? 'No live preview is available for this form.'}
        />
      ) : unavailable !== null ? (
        <QuantPreviewDisabled reason={unavailable} />
      ) : (
        offer?.instrument != null &&
        preview !== null && (
          <QuantLivePreview
            key={slug}
            slug={slug}
            instrument={offer.instrument}
            preview={preview}
            offered={offer.offered}
            instrumentLabel={instrumentLabel}
            mismatch={offer.mismatch === null ? null : mismatchLine(offer.mismatch)}
            onInstrument={(value) => setField('instrument', value)}
            params={invalid ? null : values}
            onInvalidDetail={setPreviewInvalid}
            onMeta={setPreviewMeta}
            dataThroughFallback={lastDay}
            feed={feed}
            useRange={{
              enabled: rangeReason === null,
              title:
                rangeReason ?? `start ${rangeFrom ?? ''} · end ${lastDay ?? ''}`,
              onUse: useRange,
            }}
          />
        )
      )}

      <div className="min-w-0 bg-paper text-ink" data-params-card>
        <div className="flex min-h-10 items-center gap-3 border-b border-ink-20 px-[18px]">
          <span className="shrink-0 font-sans text-[11px] font-bold tracking-[0.2em] uppercase">
            Parameters
          </span>
          <span
            className="min-w-0 flex-1 truncate font-mono text-[11px] text-ink-50"
            title={`${strategy.name ?? slug} · ${slug}`}
          >
            {strategy.name ?? slug} · {slug}
          </span>
          <span className="shrink-0 font-mono text-[9.5px] tracking-[0.14em] text-ink-50 uppercase">
            {defs.length} params · {changedCount} changed
          </span>
        </div>
        <QuantSavedConfigs slug={slug} saved={saved} raw={form.raw} onLoad={loadSaved} emit={emit} />
        {previewInvalid !== null && (
          <div
            role="alert"
            data-banner="preview-invalid"
            className="mx-[18px] mt-2.5 border border-alert px-3 py-2 font-mono text-[12px] leading-[1.5] text-alert [overflow-wrap:anywhere]"
          >
            Preview: {previewInvalid}
          </div>
        )}
        <div className="px-[18px] pt-0.5 pb-1.5">
          {defs.map((def) => (
            <QuantParamRow
              key={def.name}
              name={def.name}
              kind={def.kind}
              value={form.raw[def.name] ?? ''}
              options={def.options}
              optionLabel={def.name === 'instrument' ? instrumentLabel : undefined}
              help={def.help}
              changed={isParamChanged(def, form.raw[def.name])}
              defaultLabel={defaultLabel(def)}
              error={errors[def.name]}
              muted={
                ignoredNames.has(def.name)
                  ? 'not used live'
                  : support !== null && def.name in support.defaults
                    ? `live uses ${String(support.defaults[def.name])}`
                    : undefined
              }
              onChange={(next) => setField(def.name, next)}
              {...dateProps(def)}
            />
          ))}
        </div>
        <div
          role="group"
          aria-label="Paper account"
          data-account-block
          className="mx-[18px] border-t border-ink-20 pt-2"
        >
          <div className="flex h-6 items-center gap-3 font-sans text-[11px] font-bold tracking-[0.2em] uppercase">
            <span className="shrink-0">Paper account</span>
            {support !== null && (
              <span
                data-live-instrument
                className="min-w-0 flex-1 truncate font-mono text-[11px] font-normal tracking-normal text-ink-50 normal-case"
              >
                Live instrument: {support.instrument}
              </span>
            )}
          </div>
          {support !== null && account !== null && accountState !== null ? (
            <>
              {ACCOUNT_ROWS.map((row) => (
                <QuantParamRow
                  key={row.field}
                  section="account"
                  name={row.name}
                  kind={row.kind}
                  value={account[row.field]}
                  changed={isAccountChanged(row.field, account, support.account_defaults)}
                  defaultLabel={String(support.account_defaults[row.field])}
                  error={accountState.errors[row.field]}
                  onChange={(next) => setAccountField(row.field, String(next))}
                />
              ))}
              <div
                data-account-server-error
                title={accountServerError ?? undefined}
                className="h-4 truncate font-mono text-[11px] text-alert"
              >
                {accountServerError}
              </div>
            </>
          ) : (
            <div data-account-reason className="py-2 font-mono text-[11px] text-ink-50">
              {liveControl.status}
            </div>
          )}
        </div>
        <div className="flex flex-col gap-3 px-[18px] pt-3.5 pb-[18px]">
          <QuantCliPreview
            slug={slug}
            flags={invalid ? form.lastValidFlags : (flagsFor(defs, form.raw) ?? '')}
            invalid={invalid}
          />
          <div className="flex flex-wrap items-center justify-between gap-2.5">
            <Button variant="secondary" onClick={reset} className="px-4 text-ink">
              Reset defaults
            </Button>
            <div className="flex items-center gap-3.5">
              <span className="font-mono text-[10px] tracking-[0.1em] text-ink-50 uppercase">
                {capReached
                  ? heldByOther
                    ? 'Slot held by another user - run will be refused'
                    : `${slotsText} - run will be refused`
                  : 'Slot free · starts immediately'}
              </span>
              <Button
                onClick={() => void start()}
                disabled={startingKind === 'live'}
                className={cn(
                  'border-ink bg-ink px-[26px] py-3 text-[11.5px] font-bold tracking-[0.22em] text-paper',
                  capReached && 'opacity-45',
                )}
              >
                ▶ Start backtest
              </Button>
              <Button
                aria-label="Start live (paper)"
                data-action="start-live"
                aria-busy={startingKind === 'live' ? true : undefined}
                disabled={!liveControl.enabled}
                onClick={() => void startLive()}
                className={cn(
                  'border-ink bg-ink px-[26px] py-3 text-[11.5px] font-bold tracking-[0.22em] text-paper',
                  'disabled:border-ink-25 disabled:bg-transparent disabled:text-ink-25',
                )}
              >
                {liveControl.label}
              </Button>
            </div>
          </div>
          <div
            role="status"
            data-live-status-text
            title={liveControl.status}
            className="h-4 truncate text-right font-mono text-[10px] tracking-[0.1em] text-ink-50"
          >
            {liveControl.status}
          </div>
          {slug === 'ema_cross' && (
            <div data-first-bar-note className="h-4 truncate font-mono text-[11px] text-ink-50">
              {FIRST_BAR_NOTE}
            </div>
          )}
        </div>
      </div>
    </PaneColumn>
  )
}
